import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { ImageAndExpandableListV2, Substance, SanityKeyed } from 'types/studio/studio';
import IconClose from 'shared/ui/Icon/24/IconClose';
import { Modal, Typography, getTypographyDefaultColor } from '@ag1/ui';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import { ListItem } from './ListItem';
import styles from './SubstancePopup.module.scss';

type Props = {
  popupVisibility: boolean;
  closePopup: () => void;
} & Pick<ImageAndExpandableListV2, 'popup' | 'theme'>;

const SubstancePopup = ({ popup, popupVisibility, closePopup, theme }: Props) => {
  const [selectedListItem, setSelectedListItem] = useState<string>('');
  const [isOpen, setOpen] = useState(false);

  useEffect(() => setOpen(popupVisibility), [popupVisibility]);

  const handleToggleExpandableList = (id: string) =>
    setSelectedListItem(id === selectedListItem ? '' : id);

  if (!Array.isArray(popup?.popupExpandableList) || popup?.popupExpandableList.length === 0) {
    return null;
  }

  const handleOverlayClick = () => {
    setOpen(false);
    closePopup();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setOpen(false)}
      mode="fullScreen"
      className={styles.modal}
      overlayClassName={styles.overlayModal}>
      <div aria-hidden="true" onClick={handleOverlayClick} className={styles.clickableArea} />
      <div className={clsx(styles.modalInner, isOpen && styles.isOpen)}>
        <div className={styles.popupHeader}>
          <Typography
            variant="portalSubtitle"
            size="2"
            weight="700"
            noPadding
            color={getTypographyDefaultColor(theme)}>
            {popup?.popupHeadline}
          </Typography>
          <button
            type="button"
            className={clsx(styles.modalButtonClose, getThemeCSSClass(theme))}
            onClick={() => {
              setOpen(false);
              closePopup();
            }}>
            <IconClose className={styles.iconClose} />
          </button>
        </div>
        <div className={styles.popupContent}>
          {popup?.popupExpandableList.map((item: SanityKeyed<Substance>) => (
            <ListItem
              {...item}
              key={item._key}
              listItemHeadline={item.listItemHeadline}
              listItemContent={item.listItemContent}
              isOpen={item._key === selectedListItem}
              onToggle={() => handleToggleExpandableList(item._key)}
              theme={theme}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default SubstancePopup;
